/**
 * Prescription writing and history.
 *
 * Replaces `renderPrescriptionsManagement`, `openPrescriptionModal` (defined
 * twice), `savePrescription`, `handleSavePrescription`, `addMedicineRow`,
 * `addMedicationRow`, `_addLabTestTag`, `_addXrayTag`, `_prescAutocompleteTrigger`,
 * `_prescAutocompleteSelect`, `viewPrescription` and `deletePrescription`.
 *
 * The doctor's autocomplete dictionaries are wired in: every term saved here is
 * fed back to POST /api/autocomplete so the next prescription types faster.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Spinner,
  Tabs,
  Textarea,
  DateInput,
} from '../../components/common';
import { MedicalFileViewer, ResultsGallery } from '../../components/common/MedicalFileViewer';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useDebounced } from '../../hooks/useLocalStorage';
import {
  useAutocomplete,
  useDoctorPrescriptions,
  usePrescriptionActions,
} from '../../hooks/usePrescriptions';
import { useDoctorBookings } from '../../hooks/useBookings';
import { validatePrescription } from '../../domain/validation';
import { collectResultFiles } from '../../domain/files';
import { isoToDisplayDate, todayKey } from '../../domain/dates';
import { BOOKING_STATUS } from '../../domain/bookings';
import { PrintPrescriptionButton } from '../../components/common/PrintPrescriptionButton';
import { AllergyBand, useAllergies } from '../hospitalShared/AllergyBand';
import { allergyMatches } from '../../domain/healthProfile';
import { useI18n } from '../../i18n/I18nProvider';
import {
  PrescriptionTemplates,
  prescriptionToTemplate,
  templateToPrescription,
} from './PrescriptionTemplates';

const emptyMedicine = () => ({ name: '', dosage: '', frequency: '', timing: '', duration: '', notes: '' });

// Exported so the live view can open the same editor for the patient in the
// room, rather than growing a second prescription form.
export const emptyPrescription = (doctor) => ({
  id: null,
  patientId: null,
  patientName: '',
  bookingId: null,
  date: todayKey(),
  diagnosis: '',
  notes: '',
  medicines: [emptyMedicine()],
  labTests: [],
  xrays: [],
  followUpDate: '',
  doctorName: doctor?.name || '',
  doctorTitle: doctor?.title || '',
  doctorLogo: doctor?.logo || '',
  clinicName: '',
  clinicAddress: '',
});

export function DoctorPrescriptions() {
  const { doctorId, doctorProfile } = useAuth();
  const { patients } = useData();
  const { success, error: toastError } = useNotifications();

  const { prescriptions, loading, error, reload } = useDoctorPrescriptions(doctorId);
  const { save, saving, commentResult } = usePrescriptionActions(doctorId);
  const autocomplete = useAutocomplete(doctorId);

  // Today's completed visits are the natural starting point for a new script.
  const { bookings: todayBookings } = useDoctorBookings(doctorId, {
    dateKey: todayKey(),
    poll: false,
  });

  const [tab, setTab] = useState('written');
  // A prescription on its way to becoming a library entry.
  const [templateDraft, setTemplateDraft] = useState(null);

  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [commentTarget, setCommentTarget] = useState(null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 250);

  const filtered = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    if (!needle) return prescriptions;
    return prescriptions.filter(
      (p) =>
        String(p.patientName).toLowerCase().includes(needle) ||
        String(p.diagnosis || '').toLowerCase().includes(needle),
    );
  }, [prescriptions, debouncedQuery]);

  const pendingVisits = useMemo(() => {
    const withScript = new Set(prescriptions.map((p) => String(p.bookingId)));
    return todayBookings.filter(
      (b) => b.status === BOOKING_STATUS.COMPLETED && !withScript.has(String(b.id)),
    );
  }, [todayBookings, prescriptions]);

  const handleSave = async (prescription) => {
    try {
      await save({ ...prescription, doctorId });
      success('تم حفظ الروشتة');
      setEditing(null);
      reload();
      autocomplete.reload();
    } catch (err) {
      // المرحلة 1.2: the server holds it for the doctor's confirmation; the editor asks.
      if (err.body && err.body.code === 'ALLERGY_CONFIRM') throw err;
      toastError(err.message || 'تعذر حفظ الروشتة');
    }
  };

  const handleComment = async (comment) => {
    if (!commentTarget) return;
    try {
      await commentResult({ ...commentTarget, doctorComment: comment });
      success('تم حفظ التعليق');
      setCommentTarget(null);
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ التعليق');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="أدويتي"
        subtitle={`${prescriptions.length} روشتة مكتوبة`}
        icon="pill"
        actions={
          <Button icon="plus" onClick={() => setEditing(emptyPrescription(doctorProfile))}>
            روشتة جديدة
          </Button>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'written', label: 'الروشتات', icon: 'file-text' },
          { id: 'ready', label: 'الروشتات الجاهزة', icon: 'clipboard-list' },
        ]}
      />

      {tab === 'ready' ? (
        <PrescriptionTemplates
          draft={templateDraft}
          onDraftHandled={() => setTemplateDraft(null)}
          onApply={(template) => {
            // Applying only fills the form. Nothing reaches a patient record
            // until the doctor saves it, which is the review step a template
            // must not be allowed to skip.
            setTab('written');
            setEditing(templateToPrescription(template, doctorProfile));
          }}
        />
      ) : (
      <>

      {/* Visits still awaiting a prescription */}
      {pendingVisits.length > 0 && (
        <Panel className="border border-amber-500/20">
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-amber-300">
            <Icon name="alert-circle" size={18} />
            كشوفات اليوم بدون روشتة ({pendingVisits.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {pendingVisits.map((booking) => (
              <button
                key={booking.id}
                type="button"
                onClick={() =>
                  setEditing({
                    ...emptyPrescription(doctorProfile),
                    patientId: booking.patientId,
                    patientName: booking.patientName,
                    bookingId: booking.id,
                    clinicName: booking.clinicName || '',
                  })
                }
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900 px-3 py-2 text-xs font-bold text-slate-200 transition-colors hover:border-amber-500/50"
              >
                <Icon name="plus" size={13} className="text-amber-400" />
                {booking.patientName}
              </button>
            ))}
          </div>
        </Panel>
      )}

      <Panel padded={false} className="p-4">
        <div className="relative">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث باسم المريض أو التشخيص..."
            className="ps-9"
          />
        </div>
      </Panel>

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && prescriptions.length === 0 ? (
        <Spinner label="جاري تحميل الروشتات..." />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState
            icon="file-x"
            title="لا توجد روشتات"
            message="ابدأ بكتابة روشتة جديدة بعد إنهاء الكشف."
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {filtered.map((prescription) => (
            <Panel key={prescription.id} className="flex flex-wrap items-center gap-4">
              <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
                <Icon name="file-text" size={20} />
              </span>

              <div className="min-w-0 flex-1">
                <h3 className="truncate font-bold text-white">{prescription.patientName}</h3>
                <p className="truncate text-xs text-slate-400">
                  {prescription.diagnosis || 'بدون تشخيص مسجل'}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Badge className="border-slate-600 bg-slate-800 text-slate-300">
                    {isoToDisplayDate(prescription.date) || prescription.date}
                  </Badge>
                  {(prescription.medicines || []).length > 0 && (
                    <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="pill">
                      {prescription.medicines.length}
                    </Badge>
                  )}
                  {(prescription.labTests || []).length > 0 && (
                    <Badge
                      className="border-amber-500/30 bg-amber-500/10 text-amber-300"
                      icon="flask-conical"
                    >
                      {prescription.labTests.length}
                    </Badge>
                  )}
                  {(prescription.xrays || []).length > 0 && (
                    <Badge className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300" icon="scan">
                      {prescription.xrays.length}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-2">
                <Button
                  variant="subtle"
                  icon="bookmark-plus"
                  onClick={() => {
                    setTemplateDraft(prescriptionToTemplate(prescription));
                    setTab('ready');
                  }}
                >
                  حفظ كجاهزة
                </Button>
                <Button variant="subtle" icon="eye" onClick={() => setViewing(prescription)}>
                  عرض
                </Button>
                <Button variant="ghost" icon="pencil" onClick={() => setEditing(prescription)}>
                  تعديل
                </Button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      </>
      )}

      <PrescriptionEditor
        prescription={editing}
        patients={patients}
        autocomplete={autocomplete}
        saving={saving}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />

      <PrescriptionViewer
        doctor={doctorProfile}
        prescription={viewing}
        onClose={() => setViewing(null)}
        onViewFile={setViewingFile}
        onComment={(type, itemIndex, existing) =>
          setCommentTarget({
            prescriptionId: viewing.id,
            type,
            itemIndex,
            existing: existing || '',
          })
        }
      />

      <MedicalFileViewer
        open={Boolean(viewingFile)}
        onClose={() => setViewingFile(null)}
        file={viewingFile}
      />

      <CommentModal
        target={commentTarget}
        onClose={() => setCommentTarget(null)}
        onSubmit={handleComment}
      />
    </div>
  );
}

/* ========================================================================== */

export function PrescriptionEditor({ prescription, patients, autocomplete, saving, onClose, onSave }) {
  const { t } = useI18n();
  const [form, setForm] = useState(prescription);
  const [errors, setErrors] = useState({});
  // المرحلة 1.2: the patient's allergies and the `allergyAlert` policy, checked
  // as the doctor types; the server checks again on save and is the one that holds.
  const allergy = useAllergies({ patientId: form?.patientId || null, bookingId: form?.bookingId || null });
  const [confirmed, setConfirmed] = useState(false);
  const [held, setHeld] = useState(null);

  useEffect(() => {
    setForm(prescription);
    setErrors({});
    setConfirmed(false);
    setHeld(null);
  }, [prescription]);

  const matches = useMemo(() => (form ? allergyMatches(form.medicines || [], allergy.allergies) : []), [form, allergy.allergies]);
  const shown = held || matches;
  const mustConfirm = (allergy.mode === 'block' && matches.length > 0) || Boolean(held);

  if (!form) return null;

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined, content: undefined }));
  };

  const updateMedicine = (index, field, value) =>
    setForm((current) => {
      const medicines = [...current.medicines];
      medicines[index] = { ...medicines[index], [field]: value };
      return { ...current, medicines };
    });

  const updateListItem = (key, index, field, value) =>
    setForm((current) => {
      const list = [...(current[key] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, [key]: list };
    });

  const handleSubmit = () => {
    const cleaned = {
      ...form,
      medicines: (form.medicines || []).filter((m) => String(m.name || '').trim()),
      labTests: (form.labTests || []).filter((t) => String(t.name || '').trim()),
      xrays: (form.xrays || []).filter((x) => String(x.name || '').trim()),
    };

    const validation = validatePrescription(cleaned);
    setErrors(validation.errors);
    if (!validation.valid) return;
    if (mustConfirm && !confirmed) return;

    Promise.resolve(onSave(mustConfirm ? { ...cleaned, allergyConfirmed: true } : cleaned)).catch((err) => {
      if (err && err.body && err.body.code === 'ALLERGY_CONFIRM') {
        setHeld(err.body.matches || []);
        setConfirmed(false);
      }
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل الروشتة' : 'روشتة جديدة'}
      icon="file-text"
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          {errors.content && <span className="text-xs font-bold text-red-400">{errors.content}</span>}
          {mustConfirm && (
            <label className="rx-allergy-confirm" data-testid="rx-allergy-confirm">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span>{t('allergy.confirm')}</span>
            </label>
          )}
          <div className="ms-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إلغاء
            </Button>
            <Button icon="save" loading={saving} onClick={handleSubmit} disabled={mustConfirm && !confirmed} data-testid="rx-save">
              حفظ الروشتة
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* المرحلة 1.2: the patient's allergies, and what on this prescription they name. */}
        <AllergyBand allergies={allergy.allergies} />
        {shown.length > 0 && (
          <div className={`rx-allergy-alert ${mustConfirm ? 'rx-allergy-alert--block' : ''}`} role="alert" data-testid="rx-allergy-alert">
            <Icon name="alert-octagon" size={18} />
            <div>
              <strong>{mustConfirm ? t('allergy.blockTitle') : t('allergy.warnTitle')}</strong>
              <ul>
                {shown.map((m) => (
                  <li key={`${m.medicine}-${m.substance}`}>{t('allergy.match', { medicine: m.medicine, substance: m.substance, severity: t(`healthProfile.allergies.severity.${m.severity}`) })}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {/* Patient */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="اسم المريض" error={errors.patientName} required>
            <Input
              value={form.patientName}
              onChange={(event) => {
                const value = event.target.value;
                update('patientName', value);
                // Typing an exact patient name links the script to their account.
                const match = patients.find((p) => p.name === value);
                if (match) update('patientId', match.id);
              }}
              list="capsule-patient-names"
            />
            <datalist id="capsule-patient-names">
              {patients.map((patient) => (
                <option key={patient.id} value={patient.name} />
              ))}
            </datalist>
          </Field>

          <Field label="التاريخ" error={errors.date} required>
            <DateInput
              value={form.date}
              onChange={(event) => update('date', event.target.value)}
            />
          </Field>
        </section>

        {/* Diagnosis */}
        <AutocompleteField
          label="التشخيص"
          field="diagnosis"
          value={form.diagnosis}
          onChange={(value) => update('diagnosis', value)}
          suggest={autocomplete.suggest}
          multiline
        />

        {/* Medicines */}
        <section className="space-y-3 border-t border-white/10 pt-5">
          <h4 className="flex items-center gap-2 text-sm font-bold text-cyan-300">
            <Icon name="pill" size={15} />
            الأدوية
          </h4>

          {form.medicines.map((medicine, index) => (
            <div key={index} className="space-y-3 rounded-xl border border-white/5 bg-white/5 p-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <AutocompleteField
                    field="medicine"
                    value={medicine.name}
                    onChange={(value) => updateMedicine(index, 'name', value)}
                    suggest={autocomplete.suggest}
                    placeholder="اسم الدواء"
                  />
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      medicines: current.medicines.filter((_, i) => i !== index),
                    }))
                  }
                  className="mt-1 shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                  aria-label="حذف الدواء"
                >
                  <Icon name="trash-2" size={15} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                <Input
                  value={medicine.dosage}
                  onChange={(event) => updateMedicine(index, 'dosage', event.target.value)}
                  placeholder="الجرعة"
                />
                <Input
                  value={medicine.frequency}
                  onChange={(event) => updateMedicine(index, 'frequency', event.target.value)}
                  placeholder="عدد المرات"
                />
                <Input
                  value={medicine.timing}
                  onChange={(event) => updateMedicine(index, 'timing', event.target.value)}
                  placeholder="التوقيت"
                />
                <Input
                  value={medicine.duration}
                  onChange={(event) => updateMedicine(index, 'duration', event.target.value)}
                  placeholder="المدة"
                />
              </div>

              <Input
                value={medicine.notes}
                onChange={(event) => updateMedicine(index, 'notes', event.target.value)}
                placeholder="ملاحظات (اختياري)"
              />
            </div>
          ))}

          <Button
            variant="subtle"
            icon="plus"
            onClick={() =>
              setForm((current) => ({ ...current, medicines: [...current.medicines, emptyMedicine()] }))
            }
          >
            إضافة دواء
          </Button>
        </section>

        {/* Lab tests */}
        <ItemListSection
          title="التحاليل المطلوبة"
          icon="flask-conical"
          field="labTest"
          items={form.labTests || []}
          suggest={autocomplete.suggest}
          onAdd={() => setForm((c) => ({ ...c, labTests: [...(c.labTests || []), { name: '', notes: '' }] }))}
          onRemove={(index) =>
            setForm((c) => ({ ...c, labTests: c.labTests.filter((_, i) => i !== index) }))
          }
          onChange={(index, field, value) => updateListItem('labTests', index, field, value)}
        />

        {/* X-rays */}
        <ItemListSection
          title="الأشعة المطلوبة"
          icon="scan"
          field="xray"
          items={form.xrays || []}
          suggest={autocomplete.suggest}
          onAdd={() => setForm((c) => ({ ...c, xrays: [...(c.xrays || []), { name: '', notes: '' }] }))}
          onRemove={(index) => setForm((c) => ({ ...c, xrays: c.xrays.filter((_, i) => i !== index) }))}
          onChange={(index, field, value) => updateListItem('xrays', index, field, value)}
        />

        {/* Notes + follow-up */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <AutocompleteField
            label="ملاحظات للمريض"
            field="notes"
            value={form.notes}
            onChange={(value) => update('notes', value)}
            suggest={autocomplete.suggest}
            multiline
          />

          <Field label="موعد المتابعة">
            <DateInput
              value={form.followUpDate || ''}
              onChange={(event) => update('followUpDate', event.target.value)}
              className="sm:w-56"
            />
          </Field>
        </section>
      </div>
    </Modal>
  );
}

/* ========================================================================== */

/**
 * Text input backed by the doctor's saved dictionary for that field.
 * The legacy version rendered its dropdown into a floating div positioned by
 * hand; this one is a plain suggestion list under the input.
 */
function AutocompleteField({
  label,
  field,
  value,
  onChange,
  suggest,
  placeholder,
  multiline = false,
}) {
  const [focused, setFocused] = useState(false);
  const suggestions = focused ? suggest(field, value) : [];
  const showList = focused && suggestions.length > 0 && suggestions[0] !== value;

  const Control = multiline ? Textarea : Input;

  return (
    <Field label={label}>
      <div className="relative">
        <Control
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          // Delay the blur so a click on a suggestion registers first.
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          {...(multiline ? { rows: 3 } : {})}
        />

        {showList && (
          <ul className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-xl border border-white/10 bg-slate-900 shadow-2xl">
            {suggestions.map((suggestion) => (
              <li key={suggestion}>
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onChange(suggestion);
                    setFocused(false);
                  }}
                  className="w-full px-3 py-2 text-start text-xs text-slate-200 transition-colors hover:bg-white/5"
                >
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}

function ItemListSection({ title, icon, field, items, suggest, onAdd, onRemove, onChange }) {
  return (
    <section className="space-y-3 border-t border-white/10 pt-5">
      <h4 className="flex items-center gap-2 text-sm font-bold text-cyan-300">
        <Icon name={icon} size={15} />
        {title}
      </h4>

      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-2">
          <div className="flex-1">
            <AutocompleteField
              field={field}
              value={item.name}
              onChange={(value) => onChange(index, 'name', value)}
              suggest={suggest}
              placeholder="الاسم"
            />
          </div>
          <div className="flex-1">
            <Field>
              <Input
                value={item.notes || ''}
                onChange={(event) => onChange(index, 'notes', event.target.value)}
                placeholder="ملاحظات"
              />
            </Field>
          </div>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="mt-1 shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
            aria-label="حذف"
          >
            <Icon name="trash-2" size={15} />
          </button>
        </div>
      ))}

      <Button variant="subtle" icon="plus" onClick={onAdd}>
        إضافة
      </Button>
    </section>
  );
}

/* ========================================================================== */

function PrescriptionViewer({ prescription, doctor, onClose, onViewFile, onComment }) {
  if (!prescription) return null;

  return (
    <Modal open onClose={onClose} title="عرض الروشتة" icon="file-text" size="lg">
      <div className="space-y-5">
        <div className="rounded-xl border border-white/5 bg-white/5 p-3">
          <h3 className="text-base font-bold text-white">{prescription.patientName}</h3>
          <p className="text-xs text-slate-400">
            {isoToDisplayDate(prescription.date) || prescription.date}
            {prescription.clinicName ? ` — ${prescription.clinicName}` : ''}
          </p>
        </div>

        {prescription.diagnosis && (
          <Block title="التشخيص" icon="clipboard-list">
            <p className="text-sm leading-relaxed text-slate-300">{prescription.diagnosis}</p>
          </Block>
        )}

        {(prescription.medicines || []).length > 0 && (
          <Block title="الأدوية" icon="pill">
            <ul className="space-y-2">
              {prescription.medicines.map((medicine, index) => (
                <li key={index} className="rounded-xl border border-white/5 bg-white/5 p-3">
                  <p className="text-sm font-bold text-white">{medicine.name}</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {[medicine.dosage, medicine.frequency, medicine.timing, medicine.duration]
                      .filter(Boolean)
                      .join(' — ')}
                  </p>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {(prescription.labTests || []).length > 0 && (
          <Block title="التحاليل" icon="flask-conical">
            <ul className="space-y-3">
              {prescription.labTests.map((test, index) => (
                <li key={index} className="rounded-xl border border-white/5 bg-white/5 p-3">
                  <p className="text-sm font-bold text-white">{test.name}</p>
                  <ResultsGallery
                    isDoctor
                    files={collectResultFiles(test)}
                    doctorComment={test.doctorComment}
                    onView={onViewFile}
                    onComment={() => onComment('labTest', index, test.doctorComment)}
                  />
                </li>
              ))}
            </ul>
          </Block>
        )}

        {(prescription.xrays || []).length > 0 && (
          <Block title="الأشعة" icon="scan">
            <ul className="space-y-3">
              {prescription.xrays.map((xray, index) => (
                <li key={index} className="rounded-xl border border-white/5 bg-white/5 p-3">
                  <p className="text-sm font-bold text-white">{xray.name}</p>
                  <ResultsGallery
                    isDoctor
                    files={collectResultFiles(xray)}
                    doctorComment={xray.doctorComment}
                    onView={onViewFile}
                    onComment={() => onComment('xray', index, xray.doctorComment)}
                  />
                </li>
              ))}
            </ul>
          </Block>
        )}

        {prescription.notes && (
          <Block title="ملاحظات" icon="sticky-note">
            <p className="text-sm leading-relaxed text-slate-300">{prescription.notes}</p>
          </Block>
        )}

        <PrintPrescriptionButton prescription={prescription} doctor={doctor} />
      </div>
    </Modal>
  );
}

function Block({ title, icon, children }) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200">
        <Icon name={icon} size={15} className="text-cyan-400" />
        {title}
      </h4>
      {children}
    </div>
  );
}

/* ========================================================================== */

function CommentModal({ target, onClose, onSubmit }) {
  const [comment, setComment] = useState('');

  useEffect(() => setComment(target?.existing || ''), [target]);

  if (!target) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="تعليق طبي"
      icon="message-square-text"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" onClick={() => onSubmit(comment.trim())}>
            حفظ التعليق
          </Button>
        </div>
      }
    >
      <Field label="التعليق" hint="سيظهر للمريض أسفل نتيجة الفحص">
        <Textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          rows={4}
          placeholder="اكتب تعليقك الطبي هنا..."
        />
      </Field>
    </Modal>
  );
}

export default DoctorPrescriptions;
